// @flow
import * as R from "ramda"

import type { SeatingFromServer, SeatingOptions } from "./types"
import type { ActionTypes, SeatingRequestStatus } from "./actions"
import {
  SET_SEATING_DATA,
  CHANGE_QUICK_SEATING_OPTION,
  CHANGE_SEAT_MAP_OPTION,
  SEATING_LOADING,
  SEATING_ERROR,
  SEATING_EMPTY,
  SEATING_REQUEST_STATUS,
} from "./actions"

export const SEATING_OPTIONS = Object.freeze({
  noReservation: "noReservation",
  window: "window",
  aisle: "aisle",
  extraLegroom: "extraLegroom",
  sitTogether: "sitTogether",
  seatMap: "seatMap",
})

export type SelectedSegmentOption = {
  selectedOption: SeatingOptions,
  // for every passenger
  selectedSeats: { [passengerIndex: number]: string },
}

export type SeatingStatus = "empty" | "valid" | "loading" | "error"

export type SeatingState = {
  data: ?SeatingFromServer,
  status: SeatingStatus,
  error: ?Error,
  // for every segment
  selectedSegmentsOptions: { [segmentCode: string]: SelectedSegmentOption },
  requestStatus: SeatingRequestStatus,
}

/**
 * Reducer for manipulating and updating seating state
 * @param {SeatingState} state - current seating state
 * @param {ActionTypes} action - actions with info for new seating state
 */

const reducer = (
  state: SeatingState = {
    data: null,
    status: "empty",
    error: null,
    selectedSegmentsOptions: {},
    requestStatus: "initial",
  },
  action: ActionTypes,
) => {
  switch (action.type) {
    case SEATING_LOADING:
      return {
        ...state,
        status: "loading",
      }
    case SEATING_ERROR:
      return {
        ...state,
        status: "error",
        error: action.payload.error,
      }
    case SEATING_EMPTY:
      return {
        ...state,
        status: "empty",
        data: null,
        selectedSegmentsOptions: {},
      }
    case SEATING_REQUEST_STATUS: {
      return {
        ...state,
        requestStatus: action.payload.status,
      }
    }
    case SET_SEATING_DATA:
      return {
        ...state,
        data: action.payload,
        status: "valid",
        selectedSegmentsOptions: action.payload.reduce((selectedSegmentOptions, segment) => {
          // eslint-disable-next-line no-param-reassign
          selectedSegmentOptions[segment.segment_code] = {
            selectedOption: SEATING_OPTIONS.noReservation,
            selectedSeats: {},
          }
          return selectedSegmentOptions
        }, {}),
      }
    case CHANGE_QUICK_SEATING_OPTION:
      return {
        ...state,
        selectedSegmentsOptions: R.merge(state.selectedSegmentsOptions, {
          [action.meta.segmentCode]: { ...action.payload, selectedSeats: {} },
        }),
      }
    case CHANGE_SEAT_MAP_OPTION:
      return {
        ...state,
        selectedSegmentsOptions: R.mergeDeepRight(state.selectedSegmentsOptions, {
          [action.meta.segmentCode]: {
            selectedOption: SEATING_OPTIONS.seatMap,
            selectedSeats: { [action.meta.forPassenger]: action.payload.selectedSeat },
          },
        }),
      }
    default:
      return state
  }
}

export default reducer
